// Global relaunch tracker for ReadyAPI test relaunches
// - Persists all relaunch entries across pages (localStorage)
// - Polls Azure DevOps in the background, independent of components
// - Notifies subscribers on any change

import azureDevOpsService from './azureDevOpsService';

class RelaunchTracker {
  constructor() {
    this.STORAGE_KEY = 'readyapi:relaunches:v2';
    this.DATE_KEY = 'readyapi:relaunches:date';
    this.map = null; // { [version]: { [suiteKey]: { [testName]: entry } } }
    this.subscribers = new Set();
    this.intervalId = null;
    this.normalize = (s) => {
      try { return decodeURIComponent(String(s || '')).trim().toLowerCase(); } catch { return String(s || '').trim().toLowerCase(); }
    };
  }

  getCurrentDate() {
    // Return current date in format YYYY-MM-DD
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }

  load() {
    if (this.map) return;
    try {
      const currentDate = this.getCurrentDate();
      const storedDate = localStorage.getItem(this.DATE_KEY);

      // Si la date stockée est différente de la date actuelle, reset tout
      if (storedDate !== currentDate) {
        console.log(`[RelaunchTracker] Nouveau jour détecté (${storedDate} → ${currentDate}), reset des données`);
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.setItem(this.DATE_KEY, currentDate);
        this.map = {};
        return;
      }

      const raw = localStorage.getItem(this.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      if (typeof parsed !== 'object' || parsed === null) {
        this.map = {};
        return;
      }
      
      this.map = parsed;
    } catch {
      this.map = {};
    }
  }

  save() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.map || {}));
    } catch {
      // ignore quota errors
    }
  }

  ensurePaths(version, suiteKey) {
    this.load();
    if (!this.map[version]) this.map[version] = {};
    if (!this.map[version][suiteKey]) this.map[version][suiteKey] = {};
  }

  getEntriesFor(version, suiteKeyOrName) {
    this.load();
    const suiteKey = this.normalize(suiteKeyOrName);
    return { ...(this.map?.[version]?.[suiteKey] || {}) };
  }

  upsertRelaunch(version, suiteKeyOrName, testName, data) {
    this.ensurePaths(version, this.normalize(suiteKeyOrName));
    const suiteKey = this.normalize(suiteKeyOrName);
    this.map[version][suiteKey][testName] = {
      ...(this.map[version][suiteKey][testName] || {}),
      ...data,
    };
    this.save();
    this.notify();
  }

  subscribe(cb) {
    this.load();
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  }

  notify() {
    for (const cb of this.subscribers) {
      try { cb(this.map); } catch {}
    }
  }

  start() {
    this.load();
    if (this.intervalId) {
      console.log('[RelaunchTracker] start() appelé mais polling déjà actif');
      return; // already running
    }
    console.log('[RelaunchTracker] Démarrage du polling (interval 10s)');
    
    this.intervalId = setInterval(async () => {
      const snapshot = this.map || {};
      const work = [];
      for (const [version, suites] of Object.entries(snapshot)) {
        for (const [suiteKey, tests] of Object.entries(suites || {})) {
          for (const [testName, entry] of Object.entries(tests || {})) {
            if (entry && entry.status === 'inProgress' && entry.runId) {
              work.push({ version, suiteKey, testName, entry });
            }
          }
        }
      }
      
      if (work.length === 0) {
        console.log('[RelaunchTracker] Aucun test en cours à surveiller');
        return;
      }
      
      console.log(`[RelaunchTracker] Polling de ${work.length} test(s) en cours...`);

      for (const item of work) {
        try {
          console.log(`[RelaunchTracker] Check test "${item.testName}" (runId: ${item.entry.runId}, pipelineId: ${item.entry.pipelineId})`);
          let resolved = null;

          if (item.entry.pipelineId) {
            console.log(`[RelaunchTracker] Appel getPipelineRun(${item.entry.pipelineId}, ${item.entry.runId})`);
            const run = await azureDevOpsService.getPipelineRun(item.entry.pipelineId, item.entry.runId);
            if (run) {
              const state = String(run.state || run.status || '').toLowerCase();
              console.log(`[RelaunchTracker] État du run: ${state}, result: ${run.result || run.outcome || 'N/A'}`);
              if (state === 'completed') {
                resolved = {
                  status: 'completed',
                  result: run.result || run.outcome || 'unknown'
                };
              }
            } else {
              console.warn(`[RelaunchTracker] getPipelineRun a retourné null`);
            }
          }

          if (!resolved) {
            console.log(`[RelaunchTracker] Fallback: appel getPipelineStatus(${item.entry.runId})`);
            const status = await azureDevOpsService.getPipelineStatus(item.entry.runId);
            if (status && status.status === 'completed') {
              console.log(`[RelaunchTracker] Status via build API: completed, result: ${status.result}`);
              resolved = {
                status: 'completed',
                result: status.result || 'unknown'
              };
            } else {
              console.log(`[RelaunchTracker] Status via build API: ${status?.status || 'null'}`);
            }
          }

          if (resolved) {
            console.log(`[RelaunchTracker] ✅ Test "${item.testName}" terminé avec résultat: ${resolved.result}`);
            this.ensurePaths(item.version, item.suiteKey);
            this.map[item.version][item.suiteKey][item.testName] = {
              ...this.map[item.version][item.suiteKey][item.testName],
              status: 'completed',
              result: resolved.result,
              completedAt: new Date().toISOString(),
            };
            this.save();
            this.notify();
          } else {
            console.log(`[RelaunchTracker] ⏳ Test "${item.testName}" toujours en cours`);
          }
        } catch (e) {
          console.error(`[RelaunchTracker] Erreur polling test "${item.testName}":`, e);
          // soft-fail, try next round
        }
      }
    }, 10000);
  }
}

const relaunchTracker = new RelaunchTracker();
export default relaunchTracker;
